import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
	FiChevronLeft,
	FiChevronRight,
	FiChevronsLeft,
	FiChevronsRight,
	FiExternalLink,
	FiPlus,
	FiRefreshCw,
	FiSearch
} from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { SkeletonCards, SkeletonList } from "../../Components/Skeleton";
import ConnectionsModalButton from "../../Components/ConnectionsModalButton";
import { client } from "../../Clients";
import type { BCCADMINBaseModelsContentListItem, BCCADMINBaseModelsContentTypeFilterItem, ContentDocument, ContentTypeConfig } from "../../API/Models";
import type { IApplicationState } from "../../Redux/Reducers";
import { adaptBodyFieldsForSave, hasAnyFieldValue, validateContentItemValues } from "../../Components/ContentItemForm";
import { showSuccessModal } from "../../Helper/showSuccessModal";
import TaxonomyMultiSelect from "../../Components/TaxonomyMultiSelect";
import CategoryPathSelect from "../../Components/CategoryPathSelect";

import {
	DEFAULT_LANGUAGE,
	DEFAULT_DOMAIN,
	PAGE_SIZE_OPTIONS,
	SINGLETON_KEYS,
	type ViewMode,
	type ContentTypeFilter,
	getQueryType,
	normalizeContentTypeKey,
	resolveDefaultFieldKey,
	buildTodayValue,
	buildDefaultComponentsFromMarkdown,
	resolveContentTypeConfig,
	resolveActiveTypeConfig,
	isHiddenListField,
	isHiddenBrowseType,
	compareBrowseOrder,
	categorizeContentType,
	CONTENT_CATEGORIES
} from "./utils";
import { matchesContentTypeQuery } from "./contentTypeSearch";
import ContentListView from "./ContentListView";
import ContentGridView from "./ContentGridView";
import { ContentTypePickerModal, CreateContentFormModal } from "./CreateContentModal";

/** Icon-only pagination button : the First/Prev/Next/Last controls share one look and disabled state. */
const PagerButton: React.FC<{
	disabled: boolean;
	onClick: () => void;
	title: string;
	children: React.ReactNode;
}> = ({ disabled, onClick, title, children }) => (
	<button
		type="button"
		disabled={disabled}
		onClick={onClick}
		className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
		title={title}
	>
		{children}
	</button>
);

/**
 * Content browser : the admin's main entry point into every editorial content type. Two modes:
 * an unfiltered OVERVIEW of type cards grouped by editorial domain, and a RESULTS table/grid once
 * a type, search or filter is applied. Rows come from GET /api/content/list : pagination, search,
 * taxonomy/category filters and sort are all server-side so they hold across the full result set,
 * not just the loaded page. Creation goes through PUT /api/content/{schemaKey}/{language}.
 * The route can lock the browser to a single type (/content/:contentTypeId, resolved by id or by
 * normalised title since ids differ per environment), which replaces the type picker with a fixed
 * label. The type dropdown/cards merge the server's "types that have rows" with the full config
 * list so empty types remain reachable.
 */
const Content: React.FC = () => {
	const history = useHistory();
	const location = useLocation();
	const { contentTypeId } = useParams<{ contentTypeId?: string }>();
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const categories = useSelector((state: IApplicationState) => state.categories.items);
	const [items, setItems] = useState<BCCADMINBaseModelsContentListItem[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
	const [loadingList, setLoadingList] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState(""); // live input value
	const [appliedSearch, setAppliedSearch] = useState(""); // committed term : only the query uses this
	const [viewMode, setViewMode] = useState<ViewMode>("list");
	// Sort column + direction. Default = newest first ("latest first"), applied server-side so it holds
	// across pagination, not just the loaded page.
	const [sort, setSort] = useState<{ field: string; desc: boolean }>({ field: "updated", desc: true });
	// Monotonic request id: the type/sort default resolves in two steps (updated → date-field once the
	// config loads), so several list calls overlap. Only the latest one's response may touch state : // otherwise a slower earlier (e.g. "updated") response lands last and clobbers the date-sorted rows.
	const loadSeqRef = useRef(0);
	const [contentTypeFilter, setContentTypeFilter] = useState<ContentTypeFilter>(() => getQueryType(location.search));
	const [availableTypes, setAvailableTypes] = useState<BCCADMINBaseModelsContentTypeFilterItem[]>([]);
	const [taxonomyFilterIds, setTaxonomyFilterIds] = useState<string[]>([]);
	const [categoryFilterPathIds, setCategoryFilterPathIds] = useState<string[]>([]);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [createType, setCreateType] = useState<ContentTypeConfig | null>(null);
	const [createValues, setCreateValues] = useState<Record<string, any>>({});
	const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
	const [createFormError, setCreateFormError] = useState<string | null>(null);
	const [creating, setCreating] = useState(false);

	const decodedContentTypeId = useMemo(() => {
		if (!contentTypeId) return null;
		try {
			return decodeURIComponent(contentTypeId);
		} catch {
			return contentTypeId;
		}
	}, [contentTypeId]);
	const queryType = useMemo(() => {
		return getQueryType(location.search);
	}, [location.search]);
	const lockedTypeKey = useMemo(() => {
		if (!decodedContentTypeId) return null;
		const normalizedId = decodedContentTypeId.trim().toLowerCase();
		const byId = (contentTypeConfigs ?? []).find((item) => item.id?.toLowerCase() === normalizedId);
		if (byId?.id) return byId.id;
		const byKey = (contentTypeConfigs ?? []).find(
			(item) => normalizeContentTypeKey(item.title ?? "") === normalizedId
		);
		if (byKey?.id) return byKey.id;
		return decodedContentTypeId.trim();
	}, [contentTypeConfigs, decodedContentTypeId]);
	const lockedConfig = useMemo(
		() => resolveContentTypeConfig(contentTypeConfigs ?? [], decodedContentTypeId),
		[contentTypeConfigs, decodedContentTypeId]
	);
	// The type the browser is currently "about": route-locked type, else the ?type= filter.
	// Drives the page heading and the direct-create shortcut (no picker when a type is active).
	const activeTypeConfig = useMemo(
		() => resolveActiveTypeConfig(contentTypeConfigs ?? [], decodedContentTypeId, contentTypeFilter),
		[contentTypeConfigs, decodedContentTypeId, contentTypeFilter]
	);

	usePageHeader({ title: activeTypeConfig?.title ?? "Contenu" });

	const isTypeLocked = Boolean(lockedTypeKey);
	const effectiveContentType = lockedTypeKey ?? contentTypeFilter;
	const showPages = effectiveContentType === "all" || effectiveContentType === "page";
	const slugColumnLabel =
		effectiveContentType === "page" ? "Slug" : effectiveContentType === "all" ? "Slug / ID" : "ID";
	const listFields = useMemo(() => {
		if (effectiveContentType === "all") return [];
		const config = lockedConfig ?? resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		// Drop internal/migration bookkeeping columns (e.g. the imported Drupal node id).
		return (config?.fields ?? []).filter((field) => field.showInList && !isHiddenListField(field));
	}, [contentTypeConfigs, effectiveContentType, lockedConfig]);
	const showPathColumn = useMemo(() => {
		if (effectiveContentType === "page") return false;
		if (items.some((item) => Boolean(item.path))) return true;
		if (effectiveContentType === "all") {
			return (contentTypeConfigs ?? []).some((config) => config.supportsDynamicPath);
		}
		const config = resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		return Boolean(config?.supportsDynamicPath);
	}, [contentTypeConfigs, effectiveContentType, items]);
	const searchPlaceholder = showPages
		? "Rechercher par slug, titre ou description"
		: "Rechercher par titre";
	const supportsTaxonomyFilter = useMemo(() => {
		if (effectiveContentType === "page") return true;
		if (effectiveContentType === "all") return true;
		if (lockedConfig?.supportsTaxonomy) return true;
		const config = resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		return Boolean(config?.supportsTaxonomy);
	}, [contentTypeConfigs, effectiveContentType, lockedConfig]);
	const supportsCategoryFilter = useMemo(() => {
		if ((categories ?? []).length === 0) return false;
		if (effectiveContentType === "all") return true;
		if (effectiveContentType === "page") return false;
		if (lockedConfig?.supportsCategory) return true;
		const config = resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		return Boolean(config?.supportsCategory);
	}, [categories, contentTypeConfigs, effectiveContentType, lockedConfig]);
	const updateTypeParam = useCallback(
		(next: string) => {
			// Changing type is a clean slate: a term typed to filter the overview tiles (or an earlier
			// content search) must not leak into the newly selected type's results (or the tile grid on
			// the way back to « Tous »).
			setSearch("");
			setAppliedSearch("");
			setPage(1);
			const params = new URLSearchParams(location.search);
			if (next === "all") {
				params.delete("type");
			} else {
				params.set("type", next);
			}
			const nextSearch = params.toString();
			const currentSearch = new URLSearchParams(location.search).toString();
			if (nextSearch !== currentSearch) {
				history.replace({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" });
			}
		},
		[history, location.pathname, location.search]
	);

	const loadList = useCallback(
		async (pageNum: number, size: number) => {
			const seq = ++loadSeqRef.current;
			setLoadingList(true);
			setError(null);
			try {
				const taxonomyFilter = taxonomyFilterIds.length ? taxonomyFilterIds.join(",") : undefined;
				const categoryId = categoryFilterPathIds.length
					? categoryFilterPathIds[categoryFilterPathIds.length - 1]
					: undefined;
				const res = await client.content.apiContentListGet(
					pageNum,
					size,
					appliedSearch.trim() || undefined,
					undefined,
					effectiveContentType === "all" ? undefined : effectiveContentType,
					taxonomyFilter,
					categoryId,
					sort.field,
					sort.desc
				);
				if (seq !== loadSeqRef.current) return; // superseded by a newer request : drop this stale response
				const data = res.data;
				setItems(data?.items ?? []);
				setTotalCount(data?.totalCount ?? 0);
				setPage(data?.page ?? 1);
				setAvailableTypes(data?.contentTypes ?? []);
			} catch (err) {
				if (seq !== loadSeqRef.current) return;
				console.error(err);
				setError("Échec du chargement de la liste des contenus.");
				setItems([]);
				setTotalCount(0);
			} finally {
				if (seq === loadSeqRef.current) setLoadingList(false);
			}
		},
		[effectiveContentType, appliedSearch, taxonomyFilterIds, categoryFilterPathIds, sort]
	);

	useEffect(() => {
		if (lockedTypeKey) {
			if (contentTypeFilter !== lockedTypeKey) {
				setContentTypeFilter(lockedTypeKey);
				setPage(1);
			}
			return;
		}
		if (queryType !== contentTypeFilter) {
			setContentTypeFilter(queryType);
			setPage(1);
		}
	}, [queryType, contentTypeFilter, lockedTypeKey]);

	/** The selected type's primary date field : a `type: "date"` field, else one named like a date.
	 * Used to default the table to "newest first BY DATE", which is more meaningful than the admin's
	 * last-modified timestamp (identical across migration-imported rows). Null for "all"/pages/dateless. */
	const dateFieldKey = useMemo(() => {
		if (effectiveContentType === "all" || showPages) return null;
		const config = lockedConfig ?? resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		const fields = config?.fields ?? [];
		const byType = fields.find((field) => (field.type ?? "").trim().toLowerCase() === "date");
		const byName = fields.find((field) => {
			const norm = normalizeContentTypeKey(field.key ?? field.title ?? "");
			return norm.split("-").includes("date") || norm.includes("publication");
		});
		return (byType ?? byName)?.key ?? null;
	}, [effectiveContentType, showPages, lockedConfig, contentTypeConfigs]);

	useEffect(() => {
		setTaxonomyFilterIds([]);
		setCategoryFilterPathIds([]);
		// Default a type's table to its own date field, newest first; fall back to last-modified for the
		// "all" view and types that have no date field.
		setSort(dateFieldKey ? { field: `field:${dateFieldKey}`, desc: true } : { field: "updated", desc: true });
	}, [effectiveContentType, dateFieldKey]);

	/** Toggle direction when re-clicking the active column, else select it. Dates start newest-first,
	 * text columns start A→Z. Sorting is server-side so it spans the whole result set, not just the page. */
	const handleSort = useCallback((field: string) => {
		setSort((prev) => (prev.field === field ? { field, desc: !prev.desc } : { field, desc: field === "updated" }));
		setPage(1);
	}, []);

	/** The selected type only gets a Title column when it actually has a title field (pages + the "all"
	 * view always do). Avoids a column full of blanks / "Untitled" for pure data types. */
	const hasTitleColumn = useMemo(() => {
		if (effectiveContentType === "all" || showPages) return true;
		const config = lockedConfig ?? resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		return (config?.fields ?? []).some((field) => normalizeContentTypeKey(field.key ?? field.title ?? "") === "title");
	}, [effectiveContentType, showPages, lockedConfig, contentTypeConfigs]);

	useEffect(() => {
		loadList(page, pageSize);
	}, [page, pageSize, loadList]);

	// Deploy/publish/unpublish changes the very thing these rows advertise : the Preview/Production
	// status pills and links. Nothing here used to listen, so after a publish the list kept showing the
	// PRE-deploy state until the operator reloaded by hand (and the deployment-link index only
	// invalidates server-side caches; it pushes nothing to clients). Refresh when a deploy finishes so
	// the pills follow reality on their own. Ignore the progress ticks : one refresh per deploy, at the
	// end, not per item.
	const refreshOnDeployFinish = useCallback(
		(payload: any) => {
			if (payload?.phase === "finish") loadList(page, pageSize);
		},
		[loadList, page, pageSize]
	);
	useRealtimeEvent("deploy", refreshOnDeployFinish);

	// The overview context = no content type selected and no taxonomy/category filter active. In this
	// context the search box is a CLIENT-SIDE FILTER over the content-type tiles below : it must never
	// run a content query or switch to the results table. A content search only happens once a type is
	// picked. (This is independent of the search term, so the tile grid stays visible while filtering.)
	const isOverviewContext =
		!isTypeLocked &&
		contentTypeFilter === "all" &&
		taxonomyFilterIds.length === 0 &&
		categoryFilterPathIds.length === 0;

	// Commit the typed term. Enter / « Rechercher » commit immediately; the debounced effect below
	// commits as you type so finding a content is instant. In the overview context the term only
	// filters the type tiles, so committing (which would query content) is suppressed there.
	const onFilterOrSearchChange = () => {
		if (isOverviewContext) return;
		setAppliedSearch(search.trim());
		setPage(1);
	};

	// Live search: commit the typed term after a short debounce so results appear as you type : but
	// only when a type is active. In the overview context the term filters the tiles client-side and
	// must not become a content query, so we keep appliedSearch empty there.
	useEffect(() => {
		if (isOverviewContext) return;
		const term = search.trim();
		if (term === appliedSearch) return;
		const handle = window.setTimeout(() => {
			setAppliedSearch(term);
			setPage(1);
		}, 250);
		return () => window.clearTimeout(handle);
	}, [search, appliedSearch, isOverviewContext]);
	const onTaxonomyFilterChange = (next: string[]) => {
		setTaxonomyFilterIds(next);
		setPage(1);
	};
	const onCategoryFilterChange = (next: string[]) => {
		setCategoryFilterPathIds(next);
		setPage(1);
	};
	const resetCreateState = () => {
		setCreateType(null);
		setCreateValues({});
		setCreateErrors({});
		setCreateFormError(null);
	};
	const buildDefaultValues = useCallback((fields: ContentTypeConfig["fields"] = []) => {
		const defaults: Record<string, any> = {};
		(fields ?? []).forEach((field, index) => {
			// Hidden technical fields never get defaults : they only carry values already stored.
			if (field.hideInEditor) return;
			const key = resolveDefaultFieldKey(field, index);
			if (!key) return;
			const fieldType = (field?.type ?? "").trim().toLowerCase();
			if (fieldType === "date" && field.defaultToday) {
				defaults[key] = buildTodayValue();
				return;
			}
			if (fieldType === "components" && field.defaultMarkdown) {
				const components = buildDefaultComponentsFromMarkdown(field.defaultMarkdown);
				if (components.length > 0) {
					defaults[key] = components;
				}
			}
		});
		return defaults;
	}, []);
	const handleOpenCreate = () => {
		resetCreateState();
		// A type is active (route-locked or ?type= filtered) → skip the picker and create it directly.
		if (activeTypeConfig) {
			setCreateType(activeTypeConfig);
			setCreateValues(buildDefaultValues(activeTypeConfig.fields ?? []));
			setShowCreateForm(true);
			return;
		}
		if (isTypeLocked) {
			setCreateFormError("Ce type de contenu n'est pas disponible.");
			return;
		}
		setShowCreateModal(true);
	};
	// Deep-link: /content/:type?create=1 (or /content?type=…&create=1) auto-opens
	// the create modal for that type : used by the preview site's "Ajouter …"
	// quick-create button. Waits for activeTypeConfig (content-type configs load
	// async), fires once, then strips the flag so refresh/back doesn't reopen.
	// Mirrors the existing Tickets ?nouveau= deep-link convention.
	const createDeepLinkHandled = useRef(false);
	useEffect(() => {
		if (createDeepLinkHandled.current) return;
		const params = new URLSearchParams(location.search);
		if (params.get("create") !== "1") return;
		if (!activeTypeConfig) return;
		createDeepLinkHandled.current = true;
		handleOpenCreate();
		params.delete("create");
		const rest = params.toString();
		history.replace({ pathname: location.pathname, search: rest ? `?${rest}` : "" });
	}, [location.search, location.pathname, activeTypeConfig, history]);
	const handleSelectCreateType = (type: ContentTypeConfig) => {
		resetCreateState();
		setCreateType(type);
		setCreateValues(buildDefaultValues(type.fields ?? []));
		setShowCreateModal(false);
		setShowCreateForm(true);
	};
	const handleCreate = async () => {
		if (!createType?.title) return;
		const schemaKey = createType.id ?? normalizeContentTypeKey(createType.title);
		const hasCategoryValue =
			Array.isArray(createValues?.categoryPathIds) && createValues.categoryPathIds.length > 0;
		if (!hasAnyFieldValue(createType.fields ?? [], createValues) && !hasCategoryValue) {
			setCreateFormError("Renseignez au moins un champ avant de créer.");
			return;
		}
		const validation = validateContentItemValues(createType.fields ?? [], createValues);
		setCreateErrors(validation.errors);
		if (validation.hasErrors) {
			setCreateFormError("Corrigez les champs signalés.");
			return;
		}
		setCreateFormError(null);
		setCreating(true);
		try {
			const payloadDoc: ContentDocument = {
				contentType: schemaKey,
				language: DEFAULT_LANGUAGE,
				isDisabled: false,
				isDeleted: false,
				// A body typed as a string anywhere upstream is migrated to components on save.
				data: adaptBodyFieldsForSave(createType.fields ?? [], createValues)
			};
			const response = await client.content.apiContentSchemaKeyLanguagePut(schemaKey, DEFAULT_LANGUAGE, undefined, payloadDoc);
			const createdId = response.data?.current?.id ?? null;
			setShowCreateForm(false);
			setShowCreateModal(false);
			resetCreateState();
			setPage(1);
			await loadList(1, pageSize);
			const routeType = createType.id ?? schemaKey;
			showSuccessModal({
				title: "Contenu créé",
				message: `${createType.title} a été créé.`,
				actionLabel: createdId ? "Voir le contenu" : undefined,
				onAction: createdId
					? () => history.push(`/content/${encodeURIComponent(routeType)}/${encodeURIComponent(createdId)}`)
					: undefined,
				// Pour qui detient le droit : creer et mettre en production en un geste.
				publish: createdId ? { contentId: createdId, contentType: schemaKey, onPublished: () => loadList(1, pageSize) } : undefined
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? "Échec de la création.";
			setCreateFormError(String(apiMessage));
		} finally {
			setCreating(false);
		}
	};

	/** Row navigation. Singletons (layout / languages / contact-info) open their dedicated sidebar
	 * pages rather than the generic editor; pages open the page editor; everything else opens the
	 * generic content editor at /content/{type}/{id}. */
	const goToPage = (p: BCCADMINBaseModelsContentListItem) => {
		const langCode = p.language || DEFAULT_LANGUAGE;
		const contentTypeKey = (p.contentType ?? "").toLowerCase();
		if (contentTypeKey !== "page") {
			const route = contentTypeKey === "layout"
				? "/layout"
				: contentTypeKey === "languages"
					? "/languages"
					: contentTypeKey === "contact-info"
						? "/contact-info"
						: null;
			if (route) history.push(route);
			if (!route) {
				const config = resolveContentTypeConfig(contentTypeConfigs ?? [], p.contentType ?? "");
				const routeType = config?.id ?? p.contentType;
				if (routeType && p.id) {
					history.push(`/content/${encodeURIComponent(routeType)}/${encodeURIComponent(p.id)}`);
				}
			}
			return;
		}
		if (p.id) {
			history.push(`/page/${encodeURIComponent(p.id)}`);
		} else {
			history.push(`/page/${encodeURIComponent(langCode)}/${encodeURIComponent(p.slug || "")}`);
		}
	};

	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const canPrev = page > 1;
	const canNext = page < totalPages;
	const goToPageNum = (next: number) => {
		const clamped = Math.min(Math.max(1, next), totalPages);
		if (clamped !== page) loadList(clamped, pageSize);
	};

	const createableTypes = useMemo(
		() =>
			(contentTypeConfigs ?? [])
				.filter((item) => {
					const key = normalizeContentTypeKey(item.title ?? "");
					return Boolean(item.title) && !SINGLETON_KEYS.has(key);
				})
				.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? "", undefined, { sensitivity: "base" })),
		[contentTypeConfigs]
	);
	const configFilters = useMemo(
		() =>
			(contentTypeConfigs ?? [])
				.filter((item) => item?.title)
				.map((item) => ({
					key: item.id ?? normalizeContentTypeKey(item.title ?? ""),
					title: item.title ?? "",
					isMain: item.isMain ?? false
				})),
		[contentTypeConfigs]
	);
	const mergedTypes = useMemo(() => {
		const map = new Map<string, BCCADMINBaseModelsContentTypeFilterItem>();
		availableTypes.forEach((entry) => {
			if (!entry.key) return;
			map.set(entry.key, entry);
		});
		configFilters.forEach((entry) => {
			if (!entry.key) return;
			const existing = map.get(entry.key);
			if (existing) {
				map.set(entry.key, {
					...existing,
					...entry,
					title: existing.title ?? entry.title,
					isMain: existing.isMain ?? entry.isMain
				});
			} else {
				map.set(entry.key, entry);
			}
		});
		return Array.from(map.values())
			// Hide non-editorial / singleton types (layout, languages, contact-info, site-settings,
			// sitemap) from the browser : they're edited from their own sidebar pages or aren't content.
			.filter((entry) => !isHiddenBrowseType(entry.key, entry.title))
			.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? "", undefined, { sensitivity: "base" }));
	}, [availableTypes, configFilters]);
	const contentTypeLabelMap = useMemo(() => {
		const map = new Map<string, string>();
		mergedTypes.forEach((entry) => {
			if (!entry.key) return;
			map.set(entry.key.toLowerCase(), entry.title ?? entry.key);
		});
		return map;
	}, [mergedTypes]);
	const resolveContentTypeLabel = useCallback(
		(contentType?: string | null) => {
			if (!contentType) return "-";
			const key = contentType.trim().toLowerCase();
			return contentTypeLabelMap.get(key) ?? contentType;
		},
		[contentTypeLabelMap]
	);
	const contentTypeFilters = useMemo(
		() =>
			[{ key: "all", title: "Tous" } as BCCADMINBaseModelsContentTypeFilterItem]
				.concat(mergedTypes)
				.filter((entry) => !!entry.key),
		[mergedTypes]
	);
	const baseColumnCount = showPathColumn ? 7 : 6;
	const totalColumnCount = baseColumnCount + listFields.length;

	// The overview (type-tile grid) shows whenever no type/taxonomy/category filter is active : even
	// while a search term is typed, because in this context the term filters the tiles rather than
	// querying content. (Same condition as isOverviewContext.)
	const isUnfilteredOverview = isOverviewContext;

	const activeTypeLabel = isTypeLocked
		? lockedConfig?.title ?? resolveContentTypeLabel(effectiveContentType)
		: resolveContentTypeLabel(contentTypeFilter);

	const overviewCards = useMemo(() => {
		if (!isUnfilteredOverview) return [];
		const configByKey = new Map<string, ContentTypeConfig>();
		(contentTypeConfigs ?? []).forEach((cfg) => {
			if (cfg?.id) configByKey.set(cfg.id.toLowerCase(), cfg);
			if (cfg?.title) configByKey.set(normalizeContentTypeKey(cfg.title), cfg);
		});
		// The search box in the overview is a client-side tile filter, matching the type's title,
		// description, key or search keywords : no content query, no results table. La comparaison
		// tolere UNE faute de frappe et les debuts de mots (voir contentTypeSearch).
		return mergedTypes
			.filter((entry) => entry.key)
			.map((entry) => {
				const key = entry.key!;
				const cfg = configByKey.get(key.toLowerCase());
				return {
					key,
					title: cfg?.title ?? entry.title ?? key,
					description: cfg?.description ?? "",
					keywords: (cfg?.searchKeywords ?? []).join(" "),
					isMain: cfg?.isMain ?? entry.isMain ?? false
				};
			})
			.filter((card) => matchesContentTypeQuery([card.title, card.description, card.key, card.keywords], search))
			// Pin the everyday editorial types (article → event → page) first, then the other "main"
			// massive types, then the rest : so the most-used content sits at the top of the overview.
			.sort(compareBrowseOrder);
	}, [contentTypeConfigs, isUnfilteredOverview, mergedTypes, search]);

	// Split the overview cards into the labelled visual groups (Actualités, Taux & marchés, …).
	// CONTENT_CATEGORIES is ordered by update cadence, so the domains an editor touches daily sit
	// at the top; `overviewCards` is already pin-sorted, so each group keeps article→event→page first.
	const overviewGroups = useMemo(() => {
		const byCategory = new Map<string, typeof overviewCards>();
		overviewCards.forEach((card) => {
			const categoryId = categorizeContentType(card.title, card.key);
			const bucket = byCategory.get(categoryId);
			if (bucket) bucket.push(card);
			else byCategory.set(categoryId, [card]);
		});
		return CONTENT_CATEGORIES.map((category) => ({
			...category,
			cards: byCategory.get(category.id) ?? []
		})).filter((group) => group.cards.length > 0);
	}, [overviewCards]);

	/** Reusable search box (Enter or button triggers the query). */
	const searchBox = (
		<div className="flex flex-1 items-end gap-2 min-w-[220px]">
			<div className="relative flex-1">
				<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
				<input
					className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-slate-400 focus:outline-none"
					placeholder={isOverviewContext ? "Filtrer les types de contenu…" : searchPlaceholder}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && onFilterOrSearchChange()}
				/>
			</div>
			{!isOverviewContext && (
				<button
					className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
					onClick={() => onFilterOrSearchChange()}
				>
					Rechercher
				</button>
			)}
		</div>
	);

	return (
		// min-h-full (not h-full): the list overflows a fixed-height container, and the
		// absolute skeleton overlay would then only cover the first screen : scrolling
		// during a load revealed the stale rows below it. The content is also hidden and
		// clipped to one screen while loading so nothing can scroll out from under the
		// skeleton.
		<div className="flex min-h-full relative">
			{loadingList && (
				<div className="absolute inset-0 z-40 overflow-hidden bg-slate-50 p-4 sm:p-6">
					{isUnfilteredOverview ? <SkeletonCards count={12} /> : <SkeletonList rows={9} />}
				</div>
			)}
			<div
				className={`flex w-full gap-6 p-4 pb-16 sm:p-6 sm:pb-20 ${
					loadingList ? "invisible max-h-screen overflow-hidden" : ""
				}`}
			>
				<div className="flex-1 min-w-0 space-y-4">
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div>
							<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Contenu</p>
							<h1 className="text-xl font-semibold text-slate-900">{activeTypeConfig?.title ?? "Gérer les contenus"}</h1>
							<p className="text-sm text-slate-600">
								{activeTypeConfig
									? activeTypeConfig.description?.trim() ||
										`Parcourir les entrées « ${activeTypeConfig.title} ».`
									: "Parcourez les contenus du site par type, recherchez dans l'ensemble des entrées et créez de nouveaux éléments."}
							</p>
						</div>
						<div className="flex items-center gap-2">
							<button
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
								onClick={handleOpenCreate}
								disabled={isTypeLocked && !lockedConfig}
							>
								<FiPlus /> Créer
							</button>
							<button
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
								onClick={() => window.open(`${DEFAULT_DOMAIN}/`, "_blank")}
							>
								<FiExternalLink /> Ouvrir le site
							</button>
							<ConnectionsModalButton className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" />
						</div>
					</div>

					{error && (
						<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
							{error}
						</div>
					)}

					{isUnfilteredOverview ? (
						/* ---------- OVERVIEW: search + content-type cards (no table, no chip wall) ---------- */
						<>
							<div className="flex flex-wrap items-center gap-2">
								{searchBox}
								<button
									className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
									onClick={() => loadList(page, pageSize)}
									title="Actualiser"
								>
									<FiRefreshCw />
								</button>
							</div>
							<div className="space-y-7">
								{overviewGroups.map((group) => (
									<section key={`group-${group.id}`} className="space-y-3">
										<div className="flex items-baseline justify-between gap-3 border-b border-slate-100 pb-2">
											<div>
												<h2 className="text-sm font-semibold uppercase tracking-wider text-slate-800">
													{group.label}
												</h2>
												<p className="text-xs text-slate-500">{group.hint}</p>
											</div>
											<span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
												{group.cards.length}
											</span>
										</div>
										<div
											className="grid gap-3"
											style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}
										>
											{group.cards.map((card) => (
												<button
													key={`overview-${card.key}`}
													type="button"
													onClick={() => updateTypeParam(card.key)}
													className="group flex h-full flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-300"
												>
													<div className="flex items-start justify-between gap-2">
														<h3 className="text-sm font-semibold leading-tight text-slate-900 line-clamp-3">
															{card.title}
														</h3>
														{card.isMain && (
															<span className="shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
																Principal
															</span>
														)}
													</div>
													<p className="text-xs leading-relaxed text-slate-500 line-clamp-3">
														{card.description?.trim() || `Parcourir les entrées « ${card.title} ».`}
													</p>
													<span className="mt-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 group-hover:text-slate-900">
														Ouvrir <FiChevronRight className="h-3 w-3" />
													</span>
												</button>
											))}
										</div>
									</section>
								))}
								{overviewGroups.length === 0 && (
									<div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-600">
										{search.trim()
											? `Aucun type de contenu ne correspond à « ${search.trim()} ».`
											: "Aucun type de contenu disponible."}
									</div>
								)}
							</div>
						</>
					) : (
						/* ---------- RESULTS: compact filter bar, results take the page ---------- */
						<>
							<div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
								<div className="flex flex-wrap items-center gap-2">
									{!isTypeLocked && (
										<button
											className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
											onClick={() => updateTypeParam("all")}
											title="Revenir à tous les types de contenu"
										>
											<FiChevronLeft /> Tous
										</button>
									)}
									{!isTypeLocked ? (
										<select
											value={contentTypeFilter}
											onChange={(e) => updateTypeParam(e.target.value ?? "all")}
											className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800 focus:border-slate-400 focus:outline-none"
										>
											{contentTypeFilters.map((filter) => (
												<option key={`type-opt-${filter.key}`} value={filter.key ?? "all"}>
													{filter.title}
													{filter.isMain ? " ★" : ""}
												</option>
											))}
										</select>
									) : (
										<span className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white">
											{activeTypeLabel}
										</span>
									)}
									{searchBox}
									<button
										className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
										onClick={() => loadList(page, pageSize)}
										title="Actualiser"
									>
										<FiRefreshCw />
									</button>
									<select
											value={`${sort.field}|${sort.desc ? "desc" : "asc"}`}
											onChange={(e) => {
												const [field, dir] = e.target.value.split("|");
												setSort({ field, desc: dir === "desc" });
												setPage(1);
											}}
											className="ml-auto rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 focus:border-slate-400 focus:outline-none"
											title="Trier"
										>
											{sort.field.startsWith("field:") && (() => {
												const key = sort.field.slice("field:".length);
												const isDate = key.split("-").includes("date") || key.includes("publication");
												const label = isDate
													? sort.desc
														? "Par date : plus récent"
														: "Par date : plus ancien"
													: `Colonne : ${key} ${sort.desc ? "↓" : "↑"}`;
												return <option value={`${sort.field}|${sort.desc ? "desc" : "asc"}`}>{label}</option>;
											})()}
											<option value="updated|desc">Plus récent</option>
											<option value="updated|asc">Plus ancien</option>
											<option value="title|asc">Titre A→Z</option>
											<option value="title|desc">Titre Z→A</option>
											<option value="type|asc">Type A→Z</option>
										</select>
										<div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
										<button
											type="button"
											onClick={() => setViewMode("list")}
											className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
												viewMode === "list" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"
											}`}
										>
											Liste
										</button>
										<button
											type="button"
											onClick={() => setViewMode("grid")}
											className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
												viewMode === "grid" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"
											}`}
										>
											Grille
										</button>
									</div>
								</div>
								{(supportsCategoryFilter || supportsTaxonomyFilter) && (
									<div className="flex flex-wrap items-end gap-3 border-t border-slate-100 pt-3">
										{supportsCategoryFilter && (
											<div className="min-w-[240px] max-w-[340px] flex-1">
												<CategoryPathSelect
													label="Catégories"
													categories={categories}
													value={categoryFilterPathIds}
													onChange={onCategoryFilterChange}
													placeholder="Filtrer les catégories"
												/>
											</div>
										)}
										{supportsTaxonomyFilter && (
											<div className="min-w-[240px] max-w-[340px] flex-1">
												<TaxonomyMultiSelect
													label="Taxonomies"
													options={taxonomies}
													value={taxonomyFilterIds}
													onChange={onTaxonomyFilterChange}
													placeholder="Filtrer les taxonomies"
												/>
											</div>
										)}
									</div>
								)}
							</div>

							{viewMode === "list" ? (
								<ContentListView
									items={items}
									listFields={listFields}
									showPathColumn={showPathColumn}
									slugColumnLabel={slugColumnLabel}
									totalColumnCount={totalColumnCount}
									hasTitleColumn={hasTitleColumn}
									sort={sort}
									onSort={handleSort}
									resolveContentTypeLabel={resolveContentTypeLabel}
									onNavigate={goToPage}
								/>
							) : (
								<ContentGridView
									items={items}
									resolveContentTypeLabel={resolveContentTypeLabel}
									onNavigate={goToPage}
								/>
							)}

							{totalCount > 0 && (
								<div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-2.5 shadow-sm backdrop-blur">
									<p className="text-sm text-slate-600">
										<span className="font-semibold text-slate-900">
											{(page - 1) * pageSize + 1}&ndash;{Math.min(page * pageSize, totalCount)}
										</span>{" "}
										sur {totalCount.toLocaleString()}
									</p>
									<div className="flex items-center gap-1">
										<PagerButton disabled={!canPrev} onClick={() => goToPageNum(1)} title="Première page">
											<FiChevronsLeft />
										</PagerButton>
										<PagerButton disabled={!canPrev} onClick={() => goToPageNum(page - 1)} title="Page précédente">
											<FiChevronLeft />
										</PagerButton>
										<span className="px-2 text-sm text-slate-700">
											Page <span className="font-semibold text-slate-900">{page}</span> / {totalPages}
										</span>
										<PagerButton disabled={!canNext} onClick={() => goToPageNum(page + 1)} title="Page suivante">
											<FiChevronRight />
										</PagerButton>
										<PagerButton disabled={!canNext} onClick={() => goToPageNum(totalPages)} title="Dernière page">
											<FiChevronsRight />
										</PagerButton>
										<select
											value={pageSize}
											onChange={(event) => {
												const nextSize = Number(event.target.value);
												setPageSize(nextSize);
												setPage(1);
												loadList(1, nextSize);
											}}
											className="ml-2 rounded-lg border border-slate-200 px-2 py-2 text-xs font-semibold text-slate-700 focus:border-slate-400 focus:outline-none"
											title="Éléments par page"
										>
											{PAGE_SIZE_OPTIONS.map((size) => (
												<option key={size} value={size}>
													{size} / page
												</option>
											))}
										</select>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>
			<ContentTypePickerModal
				show={showCreateModal}
				onHide={() => setShowCreateModal(false)}
				createableTypes={createableTypes}
				onSelectType={handleSelectCreateType}
			/>
			<CreateContentFormModal
				show={showCreateForm}
				onHide={() => {
					setShowCreateForm(false);
					resetCreateState();
				}}
				createType={createType}
				createValues={createValues}
				setCreateValues={setCreateValues}
				createErrors={createErrors}
				createFormError={createFormError}
				creating={creating}
				categories={categories}
				taxonomies={taxonomies}
				onSubmit={handleCreate}
			/>
		</div>
	);
};

export default Content;
